import React from "react";
import { File, ChevronDown } from "lucide-react";
import { AdditionalAssetMeta } from "@amzn/afxtaco-types";
import { TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useTabOverflow } from "../../hooks/useTabOverflow";
import { additionalAssetLabel } from "./additionalAssetLabel";
import { additionalTabValue, TAB_TRIGGER_CLASS } from "./tabIdentifiers";
import NewContentDot from "./NewContentDot";

interface AdditionalAssetTabsBarProps {
  assets: AdditionalAssetMeta[];
  /** Tab values that currently carry a "new content" dot. */
  tabsWithNewContent: Set<string>;
  /** Select an (overflow) tab by value. */
  onSelect: (value: string) => void;
}

/**
 * Renders the additional-asset tab triggers with width-based "priority+"
 * overflow: as many tabs as fit are shown inline, the rest collapse into a
 * "More" dropdown that appears ONLY when something overflows. The split is
 * recomputed as the container resizes (the tab bar lives in a resizable panel).
 *
 * A visually-hidden "ghost" row renders every trigger (plus a "More" trigger)
 * so {@link useTabOverflow} can measure each item's intrinsic width regardless
 * of what's currently inline.
 */
const AdditionalAssetTabsBar: React.FC<AdditionalAssetTabsBarProps> = ({
  assets,
  tabsWithNewContent,
  onSelect,
}) => {
  const { containerRef, measureRef, moreRef, inlineItems, overflowItems } =
    useTabOverflow<AdditionalAssetMeta>({
      items: assets,
      getKey: (asset) => asset.path,
    });

  if (assets.length === 0) return null;

  const overflowHasNewContent = overflowItems.some((asset) =>
    tabsWithNewContent.has(additionalTabValue(asset.path)),
  );

  return (
    <>
      {/* Real row: grows to fill leftover space so its width is the budget. */}
      <div ref={containerRef} className="flex flex-1 min-w-0 items-center">
        {inlineItems.map((asset) => {
          const value = additionalTabValue(asset.path);
          return (
            <TabsTrigger
              key={value}
              value={value}
              className={TAB_TRIGGER_CLASS}
            >
              <File className="h-4 w-4" />
              <span>{additionalAssetLabel(asset.filename)}</span>
              {tabsWithNewContent.has(value) && <NewContentDot />}
            </TabsTrigger>
          );
        })}
        {overflowItems.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger className="relative flex items-center gap-1 px-4 py-3 text-sm text-muted-foreground hover:text-foreground rounded-none">
              <span>More</span>
              <ChevronDown className="h-4 w-4" />
              {overflowHasNewContent && <NewContentDot />}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {overflowItems.map((asset) => {
                const value = additionalTabValue(asset.path);
                return (
                  <DropdownMenuItem
                    key={value}
                    onSelect={() => onSelect(value)}
                    className="flex items-center gap-2"
                  >
                    <File className="h-4 w-4" />
                    <span>{additionalAssetLabel(asset.filename)}</span>
                    {tabsWithNewContent.has(value) && (
                      <NewContentDot className="ml-auto h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-400" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Ghost row: hidden, off the layout flow, used only for measurement. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[9999px] top-0 flex items-center"
      >
        {assets.map((asset) => (
          <span
            key={asset.path}
            ref={measureRef(asset.path)}
            className={TAB_TRIGGER_CLASS}
          >
            <File className="h-4 w-4" />
            <span>{additionalAssetLabel(asset.filename)}</span>
          </span>
        ))}
        <span
          ref={moreRef}
          className="relative flex items-center gap-1 px-4 py-3 text-sm"
        >
          <span>More</span>
          <ChevronDown className="h-4 w-4" />
        </span>
      </div>
    </>
  );
};

export default AdditionalAssetTabsBar;
